import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixTableReservationsTimeSlotV1TimeSlotsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/table-reservations/reservations',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/table-reservations/reservations',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/table-reservations/reservations',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_table-reservations_time-slots';

/**
 * Returns a list of time slots at a specified reservation location on a specified `date`, and their availability for a specified `partySize`.
 *
 * Without passing optional parameters, the list will contain a single time slot at the specified `date`.
 * Use `slotsBefore` and `slotsAfter` to get additional time slots before and after the specified `date`.
 *
 * If you do not provide a `duration`, the duration will be calculated automatically based on the reservation location's configuration.
 * The reservation location's settings used to determine the duration are its `defaultTurnoverTime` and `turnoverTimeRules`. These specify how much time should be allotted for a reservation of a party of a specified size.
 *
 * The interval between `startDate`s of time slots in the response is determined by the reservation location's `timeSlotInterval`. This interval is not affected by the `duration` provided.
 */
export function getTimeSlots(payload: object): RequestOptionsFactory<any> {
  function __getTimeSlots({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'date' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.table_reservations.v1.time_slot',
      method: 'POST' as any,
      methodFqn:
        'wix.table_reservations.time_slot.v1.TimeSlotsService.GetTimeSlots',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixTableReservationsTimeSlotV1TimeSlotsServiceUrl({
        protoPath: '/v1/time-slots',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'timeSlots.startDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __getTimeSlots;
}

/**
 * Checks a restaurant's availability to accommodate a reservation for a given party size in a given time slot.
 *
 *  `checkTimeSlot()` returns availability information for the restaurant's table combinations, and flags any party pacing or seat pacing conflicts that the proposed reservation would cause.
 */
export function checkTimeSlot(payload: object): RequestOptionsFactory<any> {
  function __checkTimeSlot({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'date' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.table_reservations.v1.time_slot',
      method: 'POST' as any,
      methodFqn:
        'wix.table_reservations.time_slot.v1.TimeSlotsService.CheckTimeSlot',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixTableReservationsTimeSlotV1TimeSlotsServiceUrl({
        protoPath: '/v1/check-time-slot',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __checkTimeSlot;
}

/**
 * Returns a list of scheduled time slots at a specified reservation location for a specified time range, and their availability for a specified `partySize`.
 *
 * Scheduled time slots are sequential time periods whose start times fall within a restaurant's opening hours. Opening hours are defined in the reservation location's `businessSchedule`.
 *
 * [Experiences](https://dev.wix.com/docs/api-reference/business-solutions/restaurants/wix-restaurants-new/reservations/experiences/introduction) have their own business schedules that override that of the reservation location. To get scheduled time slots for an experience, pass the ID of that experience to this method.
 *
 * If a restaurant is open during distinct time periods throughout the week, the `startDate` of the first scheduled time slot returned for each period is the same as starting time of that time period.
 * If a restaurant is always open, the `startDate` of the first returned scheduled time slot for each week is Sunday at `00:00`.
 *
 * The duration of scheduled time slots and the interval between their `startDate`s is determined by the reservation location's `timeSlotInterval`.
 *
 * For example:
 *
 * A restaurant is open every night from `22:30` to `03:55` with a `timeSlotInterval` of 1 hour.
 * If you called this method with the time range `23:00` to `02:50`, it would return time slots starting at `23:30`, `00:30`, `01:30`, and `02:30`.
 */
export function getScheduledTimeSlots(
  payload: object
): RequestOptionsFactory<any> {
  function __getScheduledTimeSlots({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'timeRange.startDate' }, { path: 'timeRange.endDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.table_reservations.v1.time_slot',
      method: 'POST' as any,
      methodFqn:
        'wix.table_reservations.time_slot.v1.TimeSlotsService.GetScheduledTimeSlots',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixTableReservationsTimeSlotV1TimeSlotsServiceUrl({
        protoPath: '/v1/scheduled-time-slots',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'timeSlots.startDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __getScheduledTimeSlots;
}
